import { Modal, Box, CircularProgress } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import BuildIdLogo from "../../assets/images/build_id_logo.png";
import AvatarPlaceholder from "../../assets/images/avtar_placeholder.jpeg";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import PhonelinkLockIcon from "@mui/icons-material/PhonelinkLock";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import RoomIcon from "@mui/icons-material/Room";
import ImageIcon from "@mui/icons-material/Image";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import ChatIcon from "@mui/icons-material/Chat";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MicIcon from "@mui/icons-material/Mic";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AppsIcon from "@mui/icons-material/Apps";
import { useNavigate } from "react-router-dom";
import { Postrequestcall } from "../../request/Postrequest";
import {
  CLIENT_SAVE_INVITATIONS,
  FETCH_CLIENT_APPLIST,
  CLIENT_DETAILS_BY_USERID,
} from "../../constant/Apipath";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { setLogindata } from "../../redux/actions/actions";
import Loader from "../../common/Loader";

const NAVY = "#0f1f5c";
const DEFAULT_ACCENT = "#E53935";

const hexToRgba = (hex, alpha) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: 440,
    maxHeight: "92vh",
    background: "#ffffff",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    outline: "none",
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
};

// ─── Fallback icon ─────────────────────────────────────────────────────────────
const FallbackIcon = ({ icon, accent }) => {
  const sx = { fontSize: 18, color: accent };
  switch (icon) {
    case "list-ul":          return <FormatListBulletedIcon sx={sx} />;
    case "file":             return <InsertDriveFileIcon sx={sx} />;
    case "chat":             return <ChatIcon sx={sx} />;
    case "image":            return <ImageIcon sx={sx} />;
    case "helmet-safety":    return <PhonelinkLockIcon sx={sx} />;
    case "map-marker":       return <RoomIcon sx={sx} />;
    case "table":            return <BackupTableIcon sx={sx} />;
    case "comment-question": return <LiveHelpIcon sx={sx} />;
    case "microphone":       return <MicIcon sx={sx} />;
    case "calendar-month":   return <CalendarMonthIcon sx={sx} />;
    default:                 return <InsertDriveFileIcon sx={sx} />;
  }
};

// ─── App icon ─────────────────────────────────────────────────────────────────
const AppIcon = ({ item, accent }) => {
  const [imgFailed, setImgFailed] = React.useState(false);
  const showImage = item?.pictureUrl && !imgFailed;

  return (
    <div style={{
      width: 48, height: 48, flexShrink: 0,
      background: showImage ? "#f7f7f7" : hexToRgba(accent, 0.08),
      border: `1px solid ${showImage ? "#ebebeb" : hexToRgba(accent, 0.15)}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden",
      borderRadius: 10,
    }}>
      {showImage ? (
        <img
          src={item.pictureUrl}
          alt={item.name}
          style={{
            width: "100%", height: "100%",
            objectFit: "contain",
            padding: 4,
            boxSizing: "border-box",
          }}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <FallbackIcon icon={item?.icon} accent={accent} />
      )}
    </div>
  );
};

// ─── Header ───────────────────────────────────────────────────────────────────
const InvitationHeader = ({ logo, orgName, accent }) => {
  const avatarSrc = logo || AvatarPlaceholder;
  return (
    <div style={{
      padding: "24px 24px 20px",
      borderBottom: "1px solid #f0f0f0",
      display: "flex", alignItems: "center", gap: 14,
    }}>
      {/* Org logo */}
      <div style={{
        width: 52, height: 52, flexShrink: 0,
        border: `2px solid ${accent}`,
        overflow: "hidden",
        borderRadius: 10,
      }}>
        <img
          src={avatarSrc}
          alt={orgName}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          onError={(e) => { e.currentTarget.src = AvatarPlaceholder; }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          background: hexToRgba(accent, 0.1),
          borderRadius: 4, padding: "2px 8px", marginBottom: 4,
        }}>
          <MarkEmailReadIcon sx={{ fontSize: 11, color: accent }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: accent, letterSpacing: "0.8px", textTransform: "uppercase" }}>
            Invitation
          </span>
        </div>
        <div style={{ fontWeight: 700, fontSize: 16, color: NAVY, lineHeight: 1.2 }}>{orgName}</div>
        <div style={{ fontSize: 12, color: "#9e9e9e", marginTop: 2 }}>
          Inviting you to join their organization
        </div>
      </div>
    </div>
  );
};

// ─── App Card (grid: big image top, text below) ──────────────────────────────
const AppCard = ({ item, accent }) => {
  const [imgFailed, setImgFailed] = React.useState(false);
  const [preview, setPreview] = React.useState(false);
  const showImage = item?.pictureUrl && !imgFailed;

  return (
    <>
      {preview && (
        <ImagePreview src={item.pictureUrl} name={item.name} onClose={() => setPreview(false)} />
      )}
      <div style={{
        display: "flex", flexDirection: "column",
        background: "#fff",
        border: "1px solid #efefef",
        borderRadius: 10,
        overflow: "hidden",
      }}>
        {/* Image square */}
        <div
          onClick={() => showImage && setPreview(true)}
          style={{
            width: "100%", aspectRatio: "1 / 1",
            background: showImage ? "#ffffff" : hexToRgba(accent, 0.07),
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden",
            cursor: showImage ? "zoom-in" : "default",
            position: "relative",
          }}
        >
          {showImage ? (
            <>
              <img
                src={item.pictureUrl}
                alt={item.name}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                onError={() => setImgFailed(true)}
              />
              {/* Zoom hint */}
              <div style={{
                position: "absolute", bottom: 6, right: 6,
                background: "rgba(0,0,0,0.35)", borderRadius: 4,
                padding: "2px 5px", display: "flex", alignItems: "center",
              }}>
                <span style={{ fontSize: 9, color: "#fff", letterSpacing: "0.3px" }}>TAP TO VIEW</span>
              </div>
            </>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <FallbackIcon icon={item?.icon} accent={accent} />
            </div>
          )}
        </div>

      {/* Text */}
      <div style={{ padding: "8px 8px 10px", borderTop: "1px solid #f5f5f5" }}>
        <div style={{
          fontWeight: 700, fontSize: 12, color: NAVY,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {item?.name}
        </div>
        {item?.features && (
          <div style={{
            fontSize: 10.5, color: "#aaa", marginTop: 2, lineHeight: 1.35,
            overflow: "hidden", textOverflow: "ellipsis",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>
            {item.features}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

// ─── Image Preview Overlay ───────────────────────────────────────────────────
const ImagePreview = ({ src, name, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.88)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
    }}
  >
    {/* Close button */}
    <div style={{
      position: "absolute", top: 16, right: 16,
      width: 32, height: 32, borderRadius: "50%",
      background: "rgba(255,255,255,0.15)",
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer",
    }}>
      <CloseIcon sx={{ fontSize: 18, color: "#fff" }} />
    </div>

    {/* Image */}
    <img
      src={src}
      alt={name}
      onClick={(e) => e.stopPropagation()}
      style={{
        maxWidth: "90%", maxHeight: "75vh",
        objectFit: "contain",
        borderRadius: 6,
        boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
      }}
    />

    {/* Name */}
    <div style={{
      marginTop: 14, fontSize: 14, fontWeight: 600,
      color: "#fff", letterSpacing: "0.2px",
    }}>
      {name}
    </div>
  </div>
);

// ─── Privacy Row ──────────────────────────────────────────────────────────────
const PrivacyRow = ({ label, visible, accent }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0" }}>
    <div style={{
      width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
      background: visible ? hexToRgba(accent, 0.1) : "#f5f5f5",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {visible
        ? <CheckIcon sx={{ fontSize: 11, color: accent }} />
        : <CloseIcon sx={{ fontSize: 11, color: "#bdbdbd" }} />
      }
    </div>
    <span style={{ fontSize: 12.5, color: visible ? "#333" : "#9e9e9e" }}>{label}</span>
  </div>
);

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{ padding: "14px 20px 0", display: "flex", alignItems: "center", gap: 8 }}>
    <div style={{ color: "#9e9e9e" }}>{icon}</div>
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.6px" }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: "#bbb", marginTop: 1 }}>{subtitle}</div>}
    </div>
  </div>
);

// ─── Step indicator ───────────────────────────────────────────────────────────
const StepDots = ({ step, accent }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", padding: "10px 0 0" }}>
    {[0, 1].map(i => (
      <div key={i} style={{
        height: 4,
        width: step === i ? 20 : 8,
        borderRadius: 2,
        background: step === i ? accent : "#e0e0e0",
        transition: "all 0.25s ease",
      }} />
    ))}
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function ClientInvitation(props) {
  const { loginData: reduxLoginData } = useSelector((state) => state.main);
  const loginData = props.overrideLoginData || reduxLoginData;

  const [features, setFeatures]     = useState([]);
  const [clientData, setClientdata] = useState({});
  const [hideBiFree, setHideBiFree] = useState(false);
  const [clientInfo, setClientinfo] = useState(false);
  const [isLoading, setIsLoading]   = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const accent = useMemo(() => clientData?.accentColor || DEFAULT_ACCENT, [clientData]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setIsLoading(true);
    await Promise.all([getClientdetails(), getClientData()]);
    setIsLoading(false);
  };

  const getClientData = async () => {
    try {
      const res = await Postrequestcall(FETCH_CLIENT_APPLIST, {
        PageNr: 1, NrOfRecPerPage: 30, FullSearch: "",
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true, FetchAllowedRecordsOnly: true,
        SearchList: [{}], SortList: [{ FieldName: "Name", Direction: "ASC" }],
      }, loginData?.accessToken);
      if (res?.status === 200) setFeatures(res?.data?.data ?? []);
      else props.handleClose();
    } catch { props.handleClose(); }
  };

  const getClientdetails = async () => {
    try {
      const invitedClientId = loginData?.userInformation?.ClientInvitation?.ClientId;
      const res = await Postrequestcall(
        CLIENT_DETAILS_BY_USERID,
        { UserId: loginData?.userInformation?.UserId, UserEmail: loginData?.userInformation?.Email },
        loginData?.accessToken
      );
      if (res?.status === 200) {
        const invitations = res?.data?.Data?.ClientInvitation ?? [];
        const match = invitations.find((c) => c.ClientId === invitedClientId);
        if (match) {
          setClientdata({
            accentColor:      match.AccentColor,
            organizationName: match.OrganizationName,
            logo:             match.ClientLogo,
          });
          setHideBiFree(!!match.HideBiFree);
        } else props.handleClose();
      } else props.handleClose();
    } catch { props.handleClose(); }
  };

  const onAccept = async () => {
    if (!clientInfo) { setClientinfo(true); return; }
    setBtnLoading(true);
    try {
      const res = await Postrequestcall(CLIENT_SAVE_INVITATIONS, {
        id: 0,
        ClientId: loginData?.userInformation?.ClientInvitation?.ClientId,
        UserEmail: loginData?.userInformation?.Email,
        Date: moment(), Status: "Accepted", Description: "User", GuideId: "",
      }, loginData?.accessToken);
      if (res.status === 201) {
        const updatedState = {
          ...loginData,
          userInformation: {
            ...loginData?.userInformation,
            ClientInvitation: { ClientId: loginData?.userInformation?.ClientInvitation?.ClientId, Status: 0 },
            ClientsList: [res?.data?.data, ...loginData?.userInformation?.ClientsList],
            PendingInvitations: (loginData?.userInformation?.PendingInvitations || []).filter(
              (inv) => inv.ClientId !== loginData?.userInformation?.ClientInvitation?.ClientId
            ),
          },
        };
        if (props.overrideLoginData) props.handleClose(updatedState);
        else {
          dispatch(setLogindata(updatedState));
          if (props?.getData) await props?.getData();
          props.handleClose();
        }
      } else props.handleClose();
    } catch { props.handleClose(); }
    finally { setBtnLoading(false); }
  };

  const onDecline = async (e) => {
    e.preventDefault();
    try {
      const res = await Postrequestcall(CLIENT_SAVE_INVITATIONS, {
        id: 0,
        ClientId: loginData?.userInformation?.ClientInvitation?.ClientId,
        UserEmail: loginData?.userInformation?.Email,
        Date: moment(), Status: "Rejected", Description: "User", GuideId: "",
      }, loginData?.accessToken);
      if (res.status) {
        const updatedState = {
          ...loginData,
          userInformation: {
            ...loginData?.userInformation,
            ClientInvitation: { ClientId: loginData?.userInformation?.ClientInvitation?.ClientId, Status: 0 },
            PendingInvitations: (loginData?.userInformation?.PendingInvitations || []).filter(
              (inv) => inv.ClientId !== loginData?.userInformation?.ClientInvitation?.ClientId
            ),
          },
        };
        if (props.overrideLoginData) props.handleClose(updatedState);
        else { dispatch(setLogindata(updatedState)); navigate("/home", { refresh: new Date() }); props.handleClose(); }
      } else props.handleClose();
    } catch (err) { props.handleClose(); }
  };

  const orgName = clientData?.organizationName || "";
  const sharedItems = ["Your first/last name", "Trades on your profile", "Emails", "Tickets", "Profile Picture", "Address", "Phone Numbers"];
  const hiddenItems = ["Timesheets", "Expenses", "Your Builder Network", "Companies you worked at", "Projects you worked on", "Education"];

  return (
    <Modal open={props.showInvitation}>
      <Box sx={styles.modal}>
        {isLoading ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
            <CircularProgress size={28} sx={{ color: accent }} />
          </div>
        ) : (
          <>
            {/* Header — always visible */}
            <InvitationHeader logo={clientData?.logo} orgName={orgName} accent={accent} />

            {/* Step dots */}
            <StepDots step={clientInfo ? 1 : 0} accent={accent} />

            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: "auto" }}>

              {!clientInfo ? (
                /* ── Screen 1: Apps ── */
                <div>
                  <SectionHeader
                    icon={<AppsIcon sx={{ fontSize: 16 }} />}
                    title={`Available Apps (${features.length})`}
                    subtitle="You'll get access to the following"
                  />

                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 10,
                    padding: "10px 16px 14px",
                  }}>
                    {features.map((item) => (
                      <AppCard key={item.id} item={item} accent={accent} />
                    ))}
                  </div>

                  {/* BuildID Free */}
                  {!hideBiFree && (
                    <div style={{
                      margin: "0 20px 16px",
                      padding: "10px 14px",
                      background: hexToRgba(accent, 0.05),
                      borderLeft: `3px solid ${accent}`,
                      display: "flex", alignItems: "center", gap: 12,
                    }}>
                      <img src={BuildIdLogo} alt="Build ID" style={{ height: 24, objectFit: "contain", flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: "#555", lineHeight: 1.4 }}>
                        You also have access to <strong>Build ID Free</strong> — our free app for self-employed
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                /* ── Screen 2: Privacy ── */
                <div>
                  {/* Shared data */}
                  <div style={{ padding: "14px 20px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                      <VisibilityIcon sx={{ fontSize: 15, color: accent }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                        Shared with {orgName}
                      </span>
                    </div>
                    <div style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr",
                      gap: "0 12px",
                      padding: "12px 14px",
                      background: "#fafafa",
                      border: "1px solid #f0f0f0",
                    }}>
                      {sharedItems.map((item, i) => <PrivacyRow key={i} label={item} visible={true} accent={accent} />)}
                    </div>
                  </div>

                  {/* Hidden data */}
                  <div style={{ padding: "14px 20px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                      <VisibilityOffIcon sx={{ fontSize: 15, color: "#9e9e9e" }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                        Not visible to {orgName}
                      </span>
                    </div>
                    <div style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr",
                      gap: "0 12px",
                      padding: "12px 14px",
                      background: "#fafafa",
                      border: "1px solid #f0f0f0",
                    }}>
                      {hiddenItems.map((item, i) => <PrivacyRow key={i} label={item} visible={false} accent={accent} />)}
                    </div>
                  </div>

                  {/* Info note */}
                  <div style={{
                    margin: "14px 20px 16px",
                    padding: "10px 14px",
                    background: hexToRgba(accent, 0.05),
                    borderLeft: `3px solid ${accent}`,
                    display: "flex", alignItems: "flex-start", gap: 8,
                  }}>
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: accent, flexShrink: 0, mt: "1px" }} />
                    <span style={{ fontSize: 12, color: "#555", lineHeight: 1.5 }}>
                      By accepting, you'll be added to this organization and can start collaborating immediately.
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ borderTop: "1px solid #f0f0f0", padding: "12px 20px 16px", background: "#fff" }}>
              <button
                onClick={onAccept}
                disabled={btnLoading}
                style={{
                  width: "100%", padding: "13px 16px",
                  border: "none",
                  borderRadius: 6,
                  background: btnLoading ? hexToRgba(accent, 0.5) : accent,
                  color: "#fff", fontSize: 14, fontWeight: 700,
                  cursor: btnLoading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  letterSpacing: "0.3px",
                }}
              >
                {btnLoading
                  ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                  : <>
                      {!clientInfo
                        ? <><ArrowForwardIcon sx={{ fontSize: 16 }} /> Continue</>
                        : <><CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> Accept Invitation</>
                      }
                    </>
                }
              </button>

              {!clientInfo && (
                <button
                  onClick={onDecline}
                  style={{
                    width: "100%", marginTop: 6, padding: "10px",
                    border: "1px solid #e8e8e8",
                    borderRadius: 6,
                    background: "transparent",
                    color: "#777", fontSize: 13, fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Decline Invitation
                </button>
              )}
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}